/**
 * Widget V2 - Main App Component
 *
 * Handles routing and widget initialization.
 */

import React from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import Layout from "/imports/ui/components/layout/Layout";
import ScrollToTop from "/imports/ui/components/layout/ScrollToTop";
import AnalyticsSync from "/imports/ui/components/layout/AnalyticsSync";

import HomeView from "/imports/ui/views/HomeView";
import CoachesView from "/imports/ui/views/CoachesView";
import CoachProfileView from "/imports/ui/views/CoachProfileView";
import LocationsView from "/imports/ui/views/LocationsView";
import LocationDetailView from "/imports/ui/views/LocationDetailView";
import ServicesView from "/imports/ui/views/ServicesView";
import ServiceDetailView from "/imports/ui/views/ServiceDetailView";
import CourtsView from "/imports/ui/views/CourtsView";
import LessonsView from "/imports/ui/views/LessonsView";
import ClassesView from "/imports/ui/views/ClassesView";
import ClassSessionView from "/imports/ui/views/ClassSessionView";
import EventsView from "/imports/ui/views/EventsView";
import TermsView from "/imports/ui/views/TermsView";
import AssessmentView from "/imports/ui/views/AssessmentView";
import CheckoutView from "/imports/ui/views/CheckoutView";
import JoinBookingView from "/imports/ui/views/JoinBookingView";
import DashboardView from "/imports/ui/views/DashboardView";
import PackagesView from "/imports/ui/views/PackagesView";
import UpfrontSessionsView from "/imports/ui/views/PackagesView/UpfrontSessionsView";
import VouchersView from "/imports/ui/views/VouchersView";
import LoginView from "/imports/ui/views/LoginView";
import { ProtectedRoute } from "/imports/ui/components/shared";
import RegisterView from "/imports/ui/views/RegisterView";
import SignupChooserView from "/imports/ui/views/SignupChooserView";
import ClaimView from "/imports/ui/views/ClaimView";
import TrainerClaimView from "/imports/ui/views/TrainerClaimView";
import TrainerDashboardView from "/imports/ui/views/TrainerDashboardView";
import TrainerFullScheduleView from "/imports/ui/views/TrainerFullScheduleView";
import EmailSignupView from "/imports/ui/views/EmailSignupView";
import OnboardingView from "/imports/ui/views/OnboardingView";
import GoogleProfileView from "/imports/ui/views/GoogleProfileView";
import PreferencesView from "/imports/ui/views/PreferencesView";
import CheckEmailView from "/imports/ui/views/CheckEmailView";
import CheckoutTelrView from "/imports/ui/views/CheckoutTelrView";
import CheckoutTelrDoneView from "/imports/ui/views/CheckoutTelrDoneView";
import CheckoutTelrHoldView from "/imports/ui/views/CheckoutTelrHoldView";
import CheckoutTelrHoldDoneView from "/imports/ui/views/CheckoutTelrHoldDoneView";
import BookingGameView from "/imports/ui/views/BookingGameView";
import CheckoutConfirmationView from "/imports/ui/views/CheckoutConfirmationView";
import VerifyEmailView from "/imports/ui/views/VerifyEmailView";
import ClaimEmailView from "/imports/ui/views/ClaimEmailView";
import ForgotPasswordView from "/imports/ui/views/ForgotPasswordView";
import ResetPasswordView from "/imports/ui/views/ResetPasswordView";
import LegalView from "/imports/ui/views/LegalView";

/**
 * Loading fallback component
 */
function LoadingFallback() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="12.5rem">
      <CircularProgress />
    </Box>
  );
}

/**
 * Error display component
 */
function ErrorDisplay({ message }) {
  return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
      <Typography variant="h5" color="error" gutterBottom>
        Unable to load widget
      </Typography>
      <Typography color="textSecondary">{message || "Please check the URL and try again."}</Typography>
    </Box>
  );
}

/**
 * Widget routes wrapper - initializes widget by slug
 */
function WidgetRoutes() {
  const { slug } = useParams();
  const location = useLocation();
  const { loading, error, initialized, initializeWidget } = useWidget();
  const { isAuthenticated, client, role, loading: authLoading } = useAuth();

  // Initialize widget when slug changes
  React.useEffect(() => {
    if (slug && !initialized) {
      initializeWidget(slug);
    }
  }, [slug, initialized, initializeWidget]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  if (!initialized) {
    return <LoadingFallback />;
  }

  // An Apointa user signed in to a club they're not a client of must onboard
  // before doing anything else. Skip the check on the auth/onboard pages
  // themselves to avoid a redirect loop.
  const path = location.pathname;
  const isAuthRoute =
    path.endsWith("/login") ||
    path.endsWith("/register") ||
    path.endsWith("/signup") ||
    path.endsWith("/claim") ||
    path.endsWith("/trainer-claim") ||
    path.endsWith("/email-signup") ||
    path.endsWith("/onboard") ||
    path.endsWith("/preferences") ||
    path.endsWith("/check-email") ||
    path.endsWith("/forgot-password");
  // A trainer legitimately has no saas-client, so the onboarding redirect must
  // not catch them.
  const needsOnboarding = !authLoading && isAuthenticated && !client && role !== "trainer";
  if (needsOnboarding && !isAuthRoute) {
    return <Navigate to={`/${slug}/onboard`} replace />;
  }

  // Trainers get a read-only schedule only — keep them off the booking/discovery
  // surfaces. Auth pages stay reachable to avoid redirect loops.
  const isTrainerRoute = path.endsWith("/trainer") || path.endsWith("/trainer/schedule");
  if (!authLoading && role === "trainer" && !isTrainerRoute && !isAuthRoute) {
    return <Navigate to={`/${slug}/trainer`} replace />;
  }

  return (
    <Layout>
      <Routes>
        <Route index element={<HomeView />} />
        <Route path="coaches" element={<CoachesView />} />
        <Route path="coaches/:trainerId" element={<CoachProfileView />} />
        <Route path="locations" element={<LocationsView />} />
        <Route path="locations/:locationId" element={<LocationDetailView />} />
        <Route path="services" element={<ServicesView />} />
        <Route path="services/:serviceId" element={<ServiceDetailView />} />
        <Route path="courts" element={<CourtsView />} />
        <Route path="lessons" element={<LessonsView />} />
        <Route path="classes" element={<ClassesView />} />
        <Route path="classes/:classId" element={<ClassSessionView />} />
        <Route path="events/:categoryId" element={<EventsView />} />
        <Route path="terms" element={<TermsView />} />
        <Route path="plans" element={<PackagesView />} />
        <Route path="plans/select-sessions" element={<UpfrontSessionsView />} />
        <Route path="vouchers" element={<VouchersView />} />
        <Route path="assessment/:sport" element={<AssessmentView />} />
        {/* Shared booking link — gates sign-in itself, then hands off to checkout */}
        <Route path="join/:bookishId" element={<JoinBookingView />} />
        {/* Booking / split-game detail — anonymous viewers get a login teaser */}
        <Route path="bookings/:bookingId" element={<BookingGameView />} />
        <Route element={<ProtectedRoute />}>
          <Route path="checkout" element={<CheckoutView />} />
          <Route path="dashboard" element={<DashboardView />} />
          <Route path="trainer" element={<TrainerDashboardView />} />
          <Route path="trainer/schedule" element={<TrainerFullScheduleView />} />
        </Route>
        <Route path="login" element={<LoginView />} />
        <Route path="forgot-password" element={<ForgotPasswordView />} />
        <Route path="register" element={<RegisterView />} />
        <Route path="signup" element={<SignupChooserView />} />
        <Route path="claim" element={<ClaimView />} />
        <Route path="trainer-claim" element={<TrainerClaimView />} />
        <Route path="email-signup" element={<EmailSignupView />} />
        <Route path="onboard" element={<OnboardingView />} />
        <Route path="google-profile" element={<GoogleProfileView />} />
        <Route path="preferences" element={<PreferencesView />} />
        <Route path="check-email" element={<CheckEmailView />} />
        <Route path="*" element={<Navigate to="" replace />} />
      </Routes>
    </Layout>
  );
}

/**
 * Main App component
 */
function App() {
  return (
    <>
      <ScrollToTop />
      <AnalyticsSync />
      <Routes>
        {/* Email verification — slug-less, no widget init, no nav layout */}
        <Route path="/auth/verify/:token" element={<VerifyEmailView />} />

        {/* Email-based claim landing — slug-less, no widget init, no nav layout */}
        <Route path="/auth/claim-email/:token" element={<ClaimEmailView />} />

        {/* Password-reset landing — slug-less, no widget init, no nav layout */}
        <Route path="/auth/reset-password/:token" element={<ResetPasswordView />} />

        {/* Telr checkout — slug-less, no widget init, no nav layout */}
        <Route path="/checkout/telr/:chargeId" element={<CheckoutTelrView />} />
        <Route path="/checkout/telr/:chargeId/done" element={<CheckoutTelrDoneView />} />
        <Route path="/checkout/telr/:chargeId/cancelled" element={<CheckoutTelrDoneView />} />
        <Route path="/checkout/telr/:chargeId/declined" element={<CheckoutTelrDoneView />} />

        {/* Split-booking card hold — slug-less, token-keyed, no widget init */}
        <Route path="/checkout/telr-hold/:holdToken" element={<CheckoutTelrHoldView />} />
        <Route path="/checkout/telr-hold/:holdToken/done" element={<CheckoutTelrHoldDoneView />} />
        <Route path="/checkout/telr-hold/:holdToken/cancelled" element={<CheckoutTelrHoldDoneView />} />
        <Route path="/checkout/telr-hold/:holdToken/declined" element={<CheckoutTelrHoldDoneView />} />

        {/* Booking confirmation for package/voucher bookings — no online charge */}
        <Route path="/checkout/confirmation/:invoiceId" element={<CheckoutConfirmationView />} />

        {/* Terms & privacy — slug-less; the checkout terms checkbox opens these */}
        <Route path="/legal/:page" element={<LegalView />} />

        <Route path="/:slug/*" element={<WidgetRoutes />} />
        <Route path="*" element={<ErrorDisplay message="Widget slug is required. Please use a URL like /your-widget-slug" />} />
      </Routes>
    </>
  );
}

export default App;
